import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  formatMoney,
  hasDiscount,
} from "../../constants/subscription";

/**
 * A plan's price in one line: the list price struck through when something is
 * on offer, then what it actually costs.
 *
 * Exists because three screens show the same figure — the public pricing page's
 * plan tiles, the signup wizard's plan chooser and the dashboard plan switcher
 * — and each one has to decide the same three things: whether to cross a price
 * out, whether "0" means free-forever or free-for-now, and what a quoted plan
 * shows instead of a number. Written once, they cannot drift; written three
 * times, one of them eventually keeps striking through a promotion that ended.
 *
 * A null `quote` is the quoted Custom tier, and renders "contact us".
 */
const PlanPriceTag = ({ quote, size, className }) => {
  const { t } = useTranslation();

  if (!quote || quote.price == null) {
    return (
      <span className={className}>{t("Pricing.contactPrice")}</span>
    );
  }

  const discounted = hasDiscount(quote);

  // Free because that is the tier, not because of an offer.
  if (!discounted && quote.price === 0) {
    return <span className={className}>{t("Pricing.free")}</span>;
  }

  const priceText = `${formatMoney(quote.price, quote.currency)}/${t("Pricing.period")}`;

  if (!discounted) {
    return <span className={className}>{priceText}</span>;
  }

  return (
    <span className={`inline-flex flex-wrap items-baseline gap-1.5 ${className || ""}`}>
      <span className={`line-through ${size === "sm" ? "text-ink-400" : "text-ink-400"}`}>
        {formatMoney(quote.listPrice, quote.currency)}
      </span>
      <span className="font-semibold">{priceText}</span>
      <span className="rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
        −{quote.discountPercent}%
      </span>
    </span>
  );
};

PlanPriceTag.propTypes = {
  /** A quote from `pricingByPlan`. Null renders the quoted-plan wording. */
  quote: PropTypes.shape({
    listPrice: PropTypes.number,
    price: PropTypes.number,
    discountPercent: PropTypes.number,
    currency: PropTypes.string,
    promotionName: PropTypes.string,
  }),
  size: PropTypes.oneOf(["sm", "md"]),
  className: PropTypes.string,
};

export default PlanPriceTag;
